/**
 * "Whose move is it?" — the one question a hold dialog asks beyond the reason.
 *
 * EXPEDITING-PARITY PR 2 (G7). Aaron, 16 Sep 2026: *"shows its waiting on
 * supplier when its actually on the customer to take it off hold."* Legacy asks
 * and ENFORCES the answer — its grid renders Release only to the nominated side
 * and shows everyone else a padlocked "On Hold (Customer)"
 * (`OpenOrders.js:1296-1321`). Helix inferred it from whoever placed the hold.
 *
 * TWO answers, and that is the whole space (Aaron, 17 Sep 2026, walking it in
 * the browser and scrapping the third he had asked for that morning: *"the
 * option to place the PO line on hold with me but then also giving the other
 * party the option to take it off hold doesn't really make any sense... it
 * should literally be one of two options"*):
 *   - with me          — I hold it, I release it. The default, and exactly what
 *                        every hold did before this question existed.
 *   - with them        — I hand it over. They release it; I cannot take it back.
 *
 * The side named is the ONLY side offered the Release control, which is what
 * makes this more than a label — and is what legacy does
 * (`BLL_OpenOrders.cs:1832-1845` keys the release off NEXTACTIONWITH, not off
 * who placed the hold). The middle case that was here for half a day — hand it
 * over but keep a release of your own — was a hold that stalls nothing, which
 * is the opposite of what a hold is for. The Helix operator can always release,
 * so a handover can never strand a line permanently.
 *
 * ONE component, used by BOTH hold dialogs (the grid's and the line modal's).
 * Legacy has two dialogs and they disagree — one lets you choose the side, the
 * other forces your own and renders it readonly — which is precisely the drift
 * this shape exists to prevent.
 *
 * What it does NOT change, and the copy says so: a held line is frozen for both
 * sides. Only the Release control moves.
 */

/** The nominated side, absolute rather than relative, as the API stores it. */
export type HoldNextAction = 'customer' | 'supplier';

interface HoldNextActionChoiceProps {
  /** The side placing the hold — the "me" in the options below. */
  placerSide: HoldNextAction;
  value: HoldNextAction;
  onChange: (next: HoldNextAction) => void;
  disabled?: boolean;
}

/** The default: next action with me, which is how holds have always behaved. */
export function defaultHoldNextAction(placerSide: HoldNextAction): HoldNextAction {
  return placerSide;
}

export function HoldNextActionChoice({
  placerSide, value, onChange, disabled = false,
}: HoldNextActionChoiceProps) {
  const other: HoldNextAction = placerSide === 'customer' ? 'supplier' : 'customer';
  const otherLabel = other === 'customer' ? 'the customer' : 'the supplier';

  const options: Array<{ label: string; hint: string; next: HoldNextAction }> = [
    {
      label: 'With me',
      hint: 'I will take it off hold when I am ready.',
      next: placerSide,
    },
    {
      label: `With ${otherLabel}`,
      hint: `Only ${otherLabel} can take it off hold. I will not be able to take it back.`,
      next: other,
    },
  ];

  return (
    <fieldset disabled={disabled} className="space-y-2">
      <legend className="block text-sm font-medium mb-1">Next action with</legend>
      {options.map((opt) => (
        <label
          key={opt.next}
          htmlFor={`hold-next-action-${opt.next}`}
          className={`flex items-start gap-2 p-2 border rounded-lg cursor-pointer transition-colors ${
            value === opt.next ? 'border-primary bg-primary/5' : 'border-border hover:bg-muted/50'
          }`}
        >
          <input
            type="radio"
            id={`hold-next-action-${opt.next}`}
            name="hold-next-action"
            className="mt-1"
            checked={value === opt.next}
            onChange={() => onChange(opt.next)}
          />
          <span className="text-sm">
            <span className="font-medium">{opt.label}</span>
            <span className="block text-xs text-muted-foreground">{opt.hint}</span>
          </span>
        </label>
      ))}
      <p className="text-xs text-muted-foreground">
        Either way the line is frozen while it is on hold — nobody can propose or
        agree a change until it comes off.
      </p>
    </fieldset>
  );
}
